// S4.3 — THE INSPECTION QUEUE, /quality/inspections (spec 07 §4.3, office).
//
// The work list. Four tabs, and they are not four filters over one idea — they
// are the four questions a qc lead actually asks:
//   Open              nothing recorded yet
//   In progress       somebody is part-way through
//   Failed–unresolved `status=failed` AND `resolution_status=pending` — the one
//                     that costs money, and the reason the tab exists at all
//   Done              passed, and the resolved/cancelled tail
//
// CURSOR PAGING, folded by id (lib/paged-rows.ts). Module 05's ledger learned
// the hard way that append-if-unseen keeps the STALE copy of a row a refetch
// updated; folding by id is the fix, applied here from the start.
//
// THE §4.3 MONEY-AT-RISK CHIPS (07-B-3, adjudicated by D-126): the failed tab
// carries a red chip with the ACTIVE quarantine's `value_at_risk_minor` — the
// money the undecided failure is tying up, which is the input to the
// scrap-vs-rework decision. The inspection wire carries no money; the value
// lives on the §2.7 record, so the tab joins client-side through
// `GET /v1/quality/holds?status=active` (matched on `inspection_id`), a read
// issued only for principals holding `quality.hold.read` — without it the
// whole COLUMN is absent, matching the server's redaction-by-absence. A row
// with no chip means what absence means everywhere here: no active quarantine
// record, an unknown valuation, or a redacted one — never 0 (D-121/§9.10).

import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { RefLabel } from "../../../../components/ref-label";
import type { RefKind } from "../../../../lib/ref-names";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Tabs, type TabDef } from "../../../../components/tabs";
import { UnauthorizedError } from "../../../../lib/problem";
import { foldPagedRows } from "../../../../lib/paged-rows";
import { requestIdOf } from "../../../../lib/request";
import { formatMinor, formatTimestamp } from "../../production/lib/format";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import { localeText } from "../run/inspection-run";
import {
  useActiveQuarantineByInspection,
  useQualityInspections,
  type InspectionsQuery,
  type QualityInspection,
} from "../hooks/quality-queries";

export const QUEUE_TABS = ["open", "in_progress", "failed", "done"] as const;
export type QueueTab = (typeof QUEUE_TABS)[number];

export interface InspectionsSearch {
  tab?: QueueTab;
  stage?: string;
  facility_id?: string;
}

export function validateInspectionsSearch(search: Record<string, unknown>): InspectionsSearch {
  const out: InspectionsSearch = {};
  const tab = search["tab"];
  if (typeof tab === "string" && (QUEUE_TABS as readonly string[]).includes(tab)) {
    out.tab = tab as QueueTab;
  }
  for (const key of ["stage", "facility_id"] as const) {
    const value = search[key];
    if (typeof value === "string" && value.length > 0) out[key] = value;
  }
  return out;
}

/**
 * 07-D — the §2.5 echo, rendered (D-192). The FOUR NUMBERS are AQL-only
 * (`qc_inspections_sampling_grain`): under `aql` the sentence is the whole
 * point — "sample 5 of 50, accept 0" — and under every other applied mode the
 * numbers are absent BY GRAIN, so the echo is the mode's own vocabulary
 * (`lot_whole` is D-194's bulk arm, which §2.1's configured vocabulary never
 * carries — it gets its own label). Pure, so the mapping is a test.
 */
export function samplingEchoText(
  t: TFunction,
  sampling: NonNullable<QualityInspection["sampling"]>,
): string {
  if (
    sampling.mode === "aql" &&
    sampling.lot_size !== null &&
    sampling.n !== null &&
    sampling.accept_number !== null
  ) {
    return t("quality.samplingEcho.aql", {
      n: sampling.n,
      lotSize: sampling.lot_size,
      accept: sampling.accept_number,
    });
  }
  if (sampling.mode === "lot_whole") return t("quality.samplingEcho.lotWhole");
  return t(`quality.sampleMode.${sampling.mode}`);
}

/**
 * A tab → the §5 query params it means (pure, so the mapping is a test rather
 * than four branches inside a render).
 *
 * `done` is the one that cannot be expressed as a single `status`: it is
 * "everything terminal", and §5 offers one status at a time. So `done` asks for
 * `passed` — the overwhelmingly common terminal state — and the screen SAYS so
 * rather than silently under-reporting: a cancelled inspection lives on the
 * open tab until it is cancelled and then nowhere, which is a gap a user can
 * see and an unlabelled one they cannot.
 */
export function queryForTab(tab: QueueTab): Pick<InspectionsQuery, "status" | "resolution_status"> {
  switch (tab) {
    case "open":
      return { status: "open" };
    case "in_progress":
      return { status: "in_progress" };
    case "failed":
      return { status: "failed", resolution_status: "pending" };
    default:
      return { status: "passed" };
  }
}

export function statusVariant(status: string): "ok" | "warn" | "bad" | "neutral" | "accent" {
  switch (status) {
    case "passed":
      return "ok";
    case "failed":
      return "bad";
    case "in_progress":
      return "warn";
    default:
      return "neutral";
  }
}

/** §4.3's "age" column, in whole hours from creation — a queue is read by how
 * long something has been waiting, never by a timestamp somebody has to
 * subtract in their head. */
export function ageHours(createdAt: string, now: number): number {
  const started = Date.parse(createdAt);
  if (!Number.isFinite(started)) return 0;
  return Math.max(0, Math.floor((now - started) / 3_600_000));
}

export function InspectionsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/quality/inspections" });
  const perms = useQualityPermissions();

  const [tab, setTab] = useState<QueueTab>(search.tab ?? "open");
  const [stage, setStage] = useState(search.stage ?? "");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rowsAccum, setRowsAccum] = useState<readonly QualityInspection[]>([]);

  // The queue's facility scope (a validated deep-link param). It rides BOTH
  // reads — the list AND the §4.3 chip walk (R5): a walk that ignored the
  // facility the list is scoped to would burn its bounded pages on other
  // facilities' holds and mis-absent the chips on the rows actually shown.
  const facilityId = search.facility_id ?? "";

  const query: InspectionsQuery = useMemo(
    () => ({
      ...queryForTab(tab),
      ...(stage.length > 0 ? { stage: stage as NonNullable<InspectionsQuery["stage"]> } : {}),
      ...(facilityId.length > 0 ? { facility_id: facilityId } : {}),
      limit: 50,
    }),
    [tab, stage, facilityId],
  );

  // Restart the page walk from the head on any filter change, keyed on WHAT the
  // query asks rather than on the memo's object identity — the M05-S08 race:
  // an identity-keyed reset wipes rows that have already folded when a
  // dependency changes without changing the emitted params, and react-query's
  // semantic cache key never re-fires the fold.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    setCursors([undefined]);
    setRowsAccum([]);
  }, [queryKey]);

  const refused = isQualityReadRefused(perms, "quality.inspection.read");
  const cursor = cursors[cursors.length - 1];
  const page = useQualityInspections(query, cursor, !refused);

  // §4.3's failed-tab chips (see the header). The read exists only on the
  // failed tab AND only for a principal the holds endpoint would answer —
  // floor_worker, storekeeper and every role without `quality.hold.read` get
  // no column at all, which is this screen's spelling of "redacted".
  //
  // DELIBERATELY FAIL-CLOSED on a failed/unresolved role feed (R8), against
  // this area's read-surface fail-open rule — see the money exception noted
  // in hooks/permissions.ts. Money is the one place "we cannot tell" must
  // not render as "you may see": the cost is a chip column missing for one
  // feed outage, never money shown to a role the server would have redacted.
  const canSeeAtRisk = perms.has("quality.hold.read");
  const atRisk = useActiveQuarantineByInspection(
    !refused && tab === "failed" && canSeeAtRisk,
    facilityId.length > 0 ? facilityId : undefined,
  );

  useEffect(() => {
    if (page.data === undefined) return;
    setRowsAccum((prior) => foldPagedRows(prior, page.data.items));
  }, [page.data]);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const now = Date.now();

  const tabs: TabDef[] = QUEUE_TABS.map((value) => ({
    id: value,
    label: t(`quality.queue.tab.${value}`),
  }));

  // Rev-7: a cohort is named by its sample size ("Sample of 24"), never by an id
  // slice. The sampler's own n when the plan was AQL, else the rows of this
  // cohort the list has loaded.
  const cohortSize = new Map<string, number>();
  for (const row of rowsAccum) {
    if (row.sample_cohort_id == null) continue;
    cohortSize.set(row.sample_cohort_id, (cohortSize.get(row.sample_cohort_id) ?? 0) + 1);
  }

  const columns: DataTableColumn<QualityInspection>[] = [
    { key: "inspection_no", header: t("quality.queue.colNo"), id: true },
    {
      key: "control_point_name",
      header: t("quality.queue.colPoint"),
      // The pinned version's bundle, resolved (#128) — the office queue and the
      // floor queue name a check the same way in the same language.
      render: (row) =>
        localeText(row.control_point_name_i18n, i18n.language, row.control_point_code),
    },
    {
      key: "subject",
      header: t("quality.queue.colSubject"),
      // The subject is whichever ref the stage set (§2.5 — exactly one group).
      // Rendered as a labelled identifier rather than a bare uuid: a queue that
      // prints 36 hex characters with no noun is a queue nobody can scan.
      render: (row) => <SubjectCell row={row} />,
    },
    {
      key: "stage",
      header: t("quality.queue.colStage"),
      // 07-D — the §2.5 wire-echo rule (D-192): a receiving row whose sampler
      // ran says WHAT the plan was ("sample 5 of 50, accept 0") instead of
      // presenting five unexplained inspections, and a cohort-bearing row
      // carries the cohort's short ref so the rows that arrived together read
      // as ONE decision. The echo is the server's — never recomputed here
      // (D-195: the AQL tables live in domain-kernel, which this app does not
      // depend on, by design).
      render: (row) => (
        <span className="quality-queue-stagecell">
          <StatusChip variant={row.stage === "final" ? "accent" : "neutral"} dot={false}>
            {t(`quality.stage.${row.stage}`)}
          </StatusChip>
          {row.sampling != null ? (
            <span className="quality-sampling-echo" data-testid="quality-queue-sampling">
              {samplingEchoText(t, row.sampling)}
            </span>
          ) : null}
          {row.sample_cohort_id != null ? (
            <span
              className="quality-sampling-cohort"
              data-testid="quality-queue-cohort"
              title={t("quality.queue.cohortTitle")}
            >
              {t("quality.queue.cohortBadge", {
                count: row.sampling?.n ?? cohortSize.get(row.sample_cohort_id) ?? 1,
              })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "status",
      header: t("quality.queue.colStatus"),
      render: (row) => (
        <>
          <StatusChip variant={statusVariant(row.status)} data-testid="quality-queue-status">
            {t(`quality.status.${row.status}`)}
          </StatusChip>
          {row.status === "failed" && row.resolution_status === "pending" ? (
            <StatusChip variant="bad" data-testid="quality-queue-unresolved">
              {t("quality.queue.unresolved")}
            </StatusChip>
          ) : null}
        </>
      ),
    },
    {
      key: "blocking",
      header: t("quality.queue.colBlocking"),
      render: (row) =>
        row.is_blocking ? (
          <StatusChip variant="warn" data-testid="quality-queue-blocking">
            {t(`quality.queue.blocks.${row.stage}`)}
          </StatusChip>
        ) : (
          <span className="quality-dash">{t("quality.queue.notBlocking")}</span>
        ),
    },
    // The §4.3 money chip column — failed tab, `quality.hold.read` holders
    // only. A chip renders exactly when the joined ACTIVE quarantine record
    // carries the money pair; every other row renders NOTHING in this cell
    // (never 0, never a dash that implies zero — D-121, §9.10). A failed
    // chip-data read also renders nothing: the chip is an aid, and the board
    // is the authoritative money screen.
    ...(tab === "failed" && canSeeAtRisk
      ? [
          {
            key: "at_risk",
            header: t("quality.queue.colAtRisk"),
            numeric: true,
            render: (row: QualityInspection) => {
              const hold = atRisk.data?.byInspection.get(row.id);
              if (hold?.value_at_risk_minor === undefined || hold.currency === undefined) {
                return null;
              }
              return (
                <StatusChip variant="bad" dot={false} data-testid="quality-queue-atrisk">
                  {formatMinor(hold.value_at_risk_minor, hold.currency, i18n.language)}
                </StatusChip>
              );
            },
          } satisfies DataTableColumn<QualityInspection>,
        ]
      : []),
    {
      key: "age",
      header: t("quality.queue.colAge"),
      age: true,
      numeric: true,
      render: (row) => t("quality.queue.ageHours", { hours: ageHours(row.created_at, now) }),
    },
    {
      key: "completed_at",
      header: t("quality.queue.colCompleted"),
      age: true,
      render: (row) =>
        row.completed_at === null
          ? t("quality.queue.notCompleted")
          : formatTimestamp(row.completed_at, i18n.language),
    },
  ];

  return (
    <section className="quality-queue" data-testid="quality-queue">
      <PageHeader title={t("quality.queue.title")} />

      <Tabs
        tabs={tabs}
        active={tab}
        label={t("quality.queue.tabsAria")}
        data-testid="quality-queue-tabs"
        onSelect={(next) => {
          setTab(next as QueueTab);
          void navigate({
            to: "/quality/inspections",
            search: { ...search, tab: next as QueueTab },
            replace: true,
          });
        }}
      />

      <div className="filter-bar" data-testid="quality-queue-filters">
        <Field label={t("quality.queue.filterStage")}>
          <select
            value={stage}
            data-testid="quality-queue-filter-stage"
            onChange={(e) => {
              setStage(e.target.value);
            }}
          >
            <option value="">{t("quality.library.allStages")}</option>
            {(["receiving", "in_process", "final"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`quality.stage.${value}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {tab === "done" ? (
        <p className="quality-note" data-testid="quality-queue-done-note">
          {t("quality.queue.doneNote")}
        </p>
      ) : null}

      {/* R5 — the chip walk's bound BIT: absence in the money column now has a
          fourth possible meaning (an unwalked older hold), so the screen says
          which rows the values cover instead of leaving it silent. */}
      {tab === "failed" && canSeeAtRisk && atRisk.data?.truncated === true ? (
        <p className="quality-note" data-testid="quality-queue-atrisk-bounded">
          {t("quality.queue.atRiskBounded", { count: atRisk.data.scanned })}
        </p>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.queue")} />
      ) : null}

      {!refused && page.fetchStatus === "paused" && rowsAccum.length === 0 ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && page.isPending && page.fetchStatus !== "paused" && rowsAccum.length === 0 ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("quality.common.loading")} />
      ) : null}

      {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.queue.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(page.error)}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !page.isPending &&
      page.fetchStatus !== "paused" &&
      page.error === null &&
      rowsAccum.length === 0 ? (
        <EmptyState
          title={t(`quality.queue.empty.${tab}Title`)}
          body={t(`quality.queue.empty.${tab}Body`)}
        />
      ) : null}

      {rowsAccum.length > 0 ? (
        <>
          <DataTable
            data-testid="quality-queue-table"
            columns={columns}
            rows={rowsAccum}
            rowKey={(row) => row.id}
            empty={null}
            caption={t("quality.queue.tableCaption")}
            rowTestId="quality-queue-row"
            onRowClick={(row) => {
              void navigate({ to: "/quality/inspections/$id", params: { id: row.id } });
            }}
          />
          {page.data?.next_cursor != null ? (
            <div className="quality-toolbar">
              <Button
                size="sm"
                data-testid="quality-queue-more"
                onClick={() => {
                  const next = page.data?.next_cursor;
                  if (next != null) setCursors((prior) => [...prior, next]);
                }}
              >
                {t("quality.queue.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function SubjectCell({ row }: { row: QualityInspection }) {
  const { t } = useTranslation();
  const pairs: { label: string; id: string; kind?: RefKind }[] = [];
  if (row.stock_unit_id !== null) {
    pairs.push({ label: t("quality.run.subjectUnit"), id: row.stock_unit_id, kind: "stock_unit" });
  } else if (row.lot_id !== null) {
    pairs.push({ label: t("quality.run.subjectLot"), id: row.lot_id, kind: "lot" });
  } else if (row.work_order_operation_id !== null) {
    pairs.push({ label: t("quality.run.subjectOperation"), id: row.work_order_operation_id });
  } else if (row.work_order_id !== null) {
    pairs.push({ label: t("quality.run.subjectWorkOrder"), id: row.work_order_id });
  }
  const first = pairs[0];
  if (first === undefined)
    return <span className="quality-dash">{t("quality.queue.noSubject")}</span>;
  return (
    <span data-testid="quality-queue-subject">
      {/* The short id through the register's RefLabel, never 36 hex
          characters; the batched names read (UP6) fills in the name. The full
          id rides on the chip's title. It is not a copy button here: the whole
          row opens the inspection, and a button in the row would swallow a
          click meant for the row. */}
      {first.label}{" "}
      <RefLabel id={first.id} {...(first.kind === undefined ? {} : { kind: first.kind })} />
    </span>
  );
}
